import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App.js';
import { createFieldApp, type FieldApp } from './bootstrap.js';
import { parseBuildInfo } from './build-info.js';
import { readOnline } from './ui/hooks.js';

// Vite substitutes these at build time; CI supplies them from the commit being deployed.
const build = parseBuildInfo({
  VITE_COMMIT_SHA: import.meta.env.VITE_COMMIT_SHA,
  VITE_BUILT_AT: import.meta.env.VITE_BUILT_AT,
  VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
});

const container = document.getElementById('root');
if (container === null) {
  throw new Error('index.html is missing #root — the app shell cannot mount.');
}

/**
 * Open local storage, and survive not being able to.
 *
 * IndexedDB is refused outright by Safari private browsing and by some MDM profiles. That
 * is a real phone in a real driveway, and the response to it is a page that says so — not a
 * white screen and not a thrown error that takes the build label down with it. `App` renders
 * its chrome with `app` undefined, so the crew can still read what went wrong and report a
 * version number.
 */
let app: FieldApp | undefined;
try {
  app = createFieldApp({
    env: import.meta.env as unknown as Record<string, string | undefined>,
    storage: globalThis.localStorage as Storage | undefined,
    isOnline: readOnline,
  });
} catch (error: unknown) {
  console.error('Local storage is unavailable; capture is disabled.', error);
}

createRoot(container).render(
  <StrictMode>
    <App build={build} app={app} />
  </StrictMode>,
);

// Register the service worker only for real builds. In dev it would cache the shell and
// make hot reload behave confusingly, which is a bad trade for a scaffold.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error: unknown) => {
      // A failed registration costs installability, not function. Never block the app.
      console.warn('Service worker registration failed', error);
    });
  });
}
